import { useLazyQuery, useMutation } from '@apollo/client'
import {
  Button,
  Collapse,
  IconButton,
  ListItem,
  Menu,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'
import MoreIcon from '@material-ui/icons/MoreVert'
import WarningIcon from '@material-ui/icons/Warning'
import { useState } from 'react'
import { useParams } from 'react-router-dom'

import { buildFeatureSet, getCredentials } from '../../cache/auth'
import CheckStatusModal from '../../components/CheckStatusModal'
import ComlogModal from '../../components/ComLogModal'
import { FileStatus, ImageProps } from '../../components/UploadBox/ImageBox'
import UploadFileModal from '../../components/UploadFileModal/UploadFileModal'
import {
  BOOKING_ATTACHMENT_QUERY,
  DELETE_BOOKING_ATTACHMENT_MUTATION,
  UPLOAD_BOOKING_ATTACHMENT_MUTATION
} from '../../graphql/bookingAttachments'
import { getRoleFromStorage } from '../../helpers/userData'
import { getTranslate } from '../../localization'
import { LocalizedLink } from '../../routes/localizedRoutes'
import bookRoleManagement from '../../staticRoleManagement/bookRoleManagement'
import { BookingAttachment } from '../../types/bookingAttachments'
import { BookingDetail } from '../../types/bookingDetails'
import { canViewComLogs } from './access'
import PMSEvents from './components/PMSEvent'
import useStyles from './styles/index.styles'

type HeaderProps = {
  adminEditMode: boolean
  setAdminEditMode: React.Dispatch<React.SetStateAction<boolean>>
  data: BookingDetail | undefined
  bookingDetail: BookingDetail | undefined
  isOpencheckStatusModal: boolean
  setIsOpencheckStatusModal: React.Dispatch<React.SetStateAction<boolean>>
}

const Header = ({
  setAdminEditMode,
  adminEditMode,
  data,
  bookingDetail,
  isOpencheckStatusModal,
  setIsOpencheckStatusModal
}: HeaderProps) => {
  const classes = useStyles()
  const params = useParams<{ reserveNumber: string }>()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [pmsEventClicked, setPmsEventClicked] = useState<boolean>(false)
  const [bookingAttachmentsModalIsOpen, setbookingAttachmentsModalIsOpen] =
    useState<boolean>(false)
  const [comlogsOpen, setcomlogsOpen] = useState<boolean>(false)
  const [comlogPage, setComlogPage] = useState<number>(1)
  const [images, setImages] = useState<ImageProps[]>([])
  const [anchorEl, setAnchorEl] = useState<
    (EventTarget & HTMLButtonElement) | null
  >(null)

  const handleMoreIconClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }
  const hasAccessToGetConfirmation = bookRoleManagement
    .find((item) => item.route === '/booking/list/:reserveNumber/vouherPdf')
    ?.rolesListWithReadAccess?.includes(getRoleFromStorage())

  const [getBookingAttachments, { loading: loadingAttachments }] =
    useLazyQuery<{
      bookingAttachments: BookingAttachment[]
    }>(BOOKING_ATTACHMENT_QUERY, {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      variables: {
        reservationCode: params.reserveNumber
      },
      onCompleted: ({ bookingAttachments }) => {
        setImages(
          bookingAttachments.map((item) => ({
            fileURL: item.url,
            fileName: item.label,
            status: FileStatus.COMPLETED,
            fileSize: 0,
            mediaID: ''
          }))
        )
      }
    })

  const RoleAccessToChangeReserveDetails = bookRoleManagement
    .find((item) => item.route === '/booking/list/:reserveNumber/view')
    ?.sections?.CHANGE_RESERVE_DETAILS.rolesListWithReadAccess?.includes(
      getRoleFromStorage()
    )

  const RoleAccessToCheckStatus = bookRoleManagement
    .find((item) => item.route === '/booking/list/:reserveNumber/view')
    ?.sections?.CHECK_STATUS.rolesListWithReadAccess?.includes(
      getRoleFromStorage()
    )

  const RoleAccessToComLogs = canViewComLogs(buildFeatureSet(getCredentials()))

  const [upload] = useMutation(UPLOAD_BOOKING_ATTACHMENT_MUTATION)
  const [deleteAttachment] = useMutation(DELETE_BOOKING_ATTACHMENT_MUTATION)
  const handleUpload = (img: ImageProps) => {
    upload({
      variables: {
        reservationCode: params.reserveNumber,
        label: img.fileName,
        file: img.file
      }
    }).then(() => {
      setImages((currentImages) => {
        const index = currentImages.findIndex(
          (item) => item.fileURL === img.fileURL
        )
        const thereAreOtherUncompletedImages = currentImages.some(
          (imageItem, i) =>
            i !== index && imageItem.status === FileStatus.UPLOADING
        )

        if (!thereAreOtherUncompletedImages) {
          getBookingAttachments()
        }
        return [
          ...currentImages.slice(0, index),
          {
            ...currentImages[index],
            status: FileStatus.COMPLETED
          },
          ...currentImages.slice(index + 1)
        ]
      })
    })
  }
  const handleDelete = (img: ImageProps) => {
    deleteAttachment({
      variables: {
        id: img.fileURL
      }
    }).then(() => {
      setImages((currentImages) => {
        const index = currentImages.findIndex(
          (item) => item.fileURL === img.fileURL
        )
        return [
          ...currentImages.slice(0, index),
          ...currentImages.slice(index + 1)
        ]
      })
    })
  }

  return (
    <>
      <div className={classes.header}>
        {!isMobile && (
          <div className={classes.herderTitle}>
            <Typography variant="h3" className={classes.headerTitle}>
              {getTranslate('Booking Details')}
            </Typography>
            <Typography variant="h5" className={classes.greyText}>
              {getTranslate('Book a hotel / Booking details')}
            </Typography>
          </div>
        )}
        <div className={classes.pmsEvent}>
          {hasAccessToGetConfirmation && (
            <Button
              variant="contained"
              color="secondary"
              className={classes.confirmBtn}
              component={LocalizedLink}
              to={`/booking/list/${params.reserveNumber}/vouherPdf`}>
              <Typography
                variant={isMobile ? 'h5' : 'h4'}
                className={classes.whiteText}>
                {getTranslate('Get Confirmation')}
              </Typography>
            </Button>
          )}
          {!isMobile && (
            <>
              <Button
                variant="contained"
                startIcon={<WarningIcon className={classes.startIcon} />}
                endIcon={
                  pmsEventClicked ? (
                    <KeyboardArrowUpIcon className={classes.icon} />
                  ) : (
                    <KeyboardArrowDownIcon className={classes.icon} />
                  )
                }
                onClick={() => {
                  setPmsEventClicked(!pmsEventClicked)
                }}
                className={classes.btn}>
                <Typography variant="h4" className={classes.greyText}>
                  {getTranslate('PMS Events')}
                </Typography>
              </Button>
              <IconButton
                className={classes.moreIconbtn}
                onClick={handleMoreIconClick}>
                <MoreIcon className={classes.moreIcon} />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleClose}>
                {RoleAccessToChangeReserveDetails && (
                  <ListItem
                    button
                    onClick={() => {
                      setAdminEditMode(!adminEditMode)
                    }}>
                    {adminEditMode
                      ? getTranslate('Cancel Admin Edit')
                      : getTranslate('Admin Edit')}
                  </ListItem>
                )}
                {RoleAccessToCheckStatus && (
                  <ListItem
                    button
                    onClick={() => {
                      setIsOpencheckStatusModal(!isOpencheckStatusModal)
                    }}>
                    {getTranslate('Check Status')}
                  </ListItem>
                )}
                <ListItem
                  // disabled
                  button
                  component={LocalizedLink}
                  to={`/booking/list/${params.reserveNumber}/edit`}>
                  {getTranslate('Edit')}
                </ListItem>
                <ListItem
                  button
                  onClick={() => {
                    setbookingAttachmentsModalIsOpen(true)
                  }}>
                  {getTranslate('Attachments')}
                </ListItem>
                {RoleAccessToComLogs && (
                  <ListItem
                    button
                    onClick={() => {
                      setcomlogsOpen(true)
                    }}>
                    {getTranslate('View Logs')}
                  </ListItem>
                )}
              </Menu>
            </>
          )}
        </div>
      </div>
      <UploadFileModal
        images={images}
        setImages={setImages}
        isOpen={bookingAttachmentsModalIsOpen}
        onClose={() => setbookingAttachmentsModalIsOpen(false)}
        multi
        onUpload={handleUpload}
        onDelete={handleDelete}
        loading={loadingAttachments}
      />
      <ComlogModal
        isOpen={comlogsOpen}
        onClose={() => setcomlogsOpen(false)}
        page={comlogPage}
        setpage={setComlogPage}
        filters={{
          EndpointName: '',
          EndpointURL: '',
          From: '',
          To: '',
          page: comlogPage,
          perPage: 8,
          EntityFilters: [
            {
              EntityType: 'Booking',
              EntityID: data?.basicDetails.id || '',
              EntityCode: ''
            }
          ]
        }}
      />
      <Collapse in={pmsEventClicked}>
        <PMSEvents data={bookingDetail} originalData={data} />
      </Collapse>
      {isOpencheckStatusModal && (
        <CheckStatusModal
          isOpen={isOpencheckStatusModal}
          onClose={() => setIsOpencheckStatusModal(false)}
          reservationNumber={params.reserveNumber}
        />
      )}
    </>
  )
}

export default Header
